import { useCallback, useEffect, useRef } from "react";

interface Args<K extends string> {
  delay: number;
  onDwell: (key: K) => void;
}

interface Api<K extends string> {
  start: (key: K) => void;
  clear: () => void;
}

export const useHoverDwell = <K extends string>({
  delay,
  onDwell,
}: Args<K>): Api<K> => {
  const timer = useRef<number | null>(null);

  const clear = useCallback((): void => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const start = useCallback(
    (key: K): void => {
      clear();
      timer.current = window.setTimeout(() => onDwell(key), delay);
    },
    [clear, delay, onDwell],
  );

  useEffect(() => clear, [clear]);

  return { start, clear };
};
